import { useState, useMemo } from 'react';
import { useLocale } from '@platform/core';
import { useBudget } from '../contexts/BudgetContext';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

type MainTab = 'overview' | 'credit' | 'debit';

const COLORS = ['#3b82f6', '#dc2626', '#f59e0b', '#22c55e', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
const BUDGET_COLOR = '#dc2626'; // Red dashed
const ACTUAL_COLOR = '#3b82f6'; // Blue solid

// Format number for chart axis
const formatNumber = (value: number): string => {
  if (Math.abs(value) >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  }
  if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(0) + 'K';
  }
  return value.toLocaleString('he-IL');
};

// Format full number for tooltip
const formatFullNumber = (value: number): string => {
  return Math.round(value).toLocaleString('he-IL');
};

export const ChartsPage: React.FC = () => {
  const { t } = useLocale();
  const {
    currentBudget,
    entries,
    getPeriodsList,
    selectedPeriod,
    setSelectedPeriod,
    isLoading,
  } = useBudget();

  const [mainTab, setMainTab] = useState<MainTab>('overview');
  const [subTab, setSubTab] = useState<string>('overview'); // 'overview' or subject name
  const [showAccumulative, setShowAccumulative] = useState(true);
  const [chartsPerRow, setChartsPerRow] = useState(2);
  const [hiddenMeasures, setHiddenMeasures] = useState<Set<string>>(new Set());
  const [fullscreenChart, setFullscreenChart] = useState<{ type: 'line' | 'bar' | 'pie' | 'doublePie'; data: any; title: string; dataKeys?: string[] } | null>(null);

  // Filter out period "0" which can be created during initialization
  const periods = getPeriodsList().filter(p => p !== '0');
  const measures = useMemo(() => {
    if (!currentBudget?.measures) return ['בפועל', 'מתוכנן'];
    return currentBudget.measures.map(m => typeof m === 'string' ? m : (m as any).Name || m);
  }, [currentBudget]);

  // Helper to identify measures (תכנון is used in older budgets)
  const isBudgetMeasure = (m: string) => m === 'מתוכנן' || m === 'תכנון' || m === 'budget' || m === 'planned';
  const isActualMeasure = (m: string) => m === 'בפועל' || m === 'actual';

  const budgetMeasure = measures.find(m => isBudgetMeasure(m)) || measures[1] || 'מתוכנן';
  const actualMeasure = measures.find(m => isActualMeasure(m)) || measures[0] || 'בפועל';

  const toggleMeasure = (measure: string) => {
    setHiddenMeasures(prev => {
      const next = new Set(prev);
      if (next.has(measure)) {
        next.delete(measure);
      } else {
        next.add(measure);
      }
      return next;
    });
  };

  // Get subjects by type
  const creditSubjects = useMemo(() =>
    currentBudget?.subjects.filter(s => s.type === 'credit') || [],
    [currentBudget]
  );

  const debitSubjects = useMemo(() =>
    currentBudget?.subjects.filter(s => s.type === 'debit') || [],
    [currentBudget]
  );

  // Calculate CreditsByPeriod (like Vue's calcCredits)
  const creditsByPeriod = useMemo(() => {
    if (!currentBudget) return [];
    const displayPeriods = selectedPeriod ? [selectedPeriod] : periods;
    let accBudget = 0, accActual = 0;

    return displayPeriods.map(period => {
      let budgetIncome = 0, actualIncome = 0;
      entries
        .filter(e => e.period === period)
        .forEach(entry => {
          const subject = currentBudget.subjects.find(s =>
            s.id === entry.subjectId || s.name === entry.subjectId
          );
          if (subject?.type === 'credit') {
            if (isBudgetMeasure(entry.measure)) budgetIncome += entry.amount;
            if (isActualMeasure(entry.measure)) actualIncome += entry.amount;
          }
        });

      if (showAccumulative) {
        accBudget += budgetIncome;
        accActual += actualIncome;
        return { period, [budgetMeasure]: accBudget, [actualMeasure]: accActual };
      }
      return { period, [budgetMeasure]: budgetIncome, [actualMeasure]: actualIncome };
    });
  }, [currentBudget, entries, periods, selectedPeriod, showAccumulative, budgetMeasure, actualMeasure]);

  // Calculate ProfitsByPeriod (like Vue's calcProfits)
  const profitsByPeriod = useMemo(() => {
    if (!currentBudget) return [];
    const displayPeriods = selectedPeriod ? [selectedPeriod] : periods;
    let accBudget = 0, accActual = 0;

    return displayPeriods.map(period => {
      let budgetIncome = 0, budgetExpenses = 0;
      let actualIncome = 0, actualExpenses = 0;

      entries.filter(e => e.period === period).forEach(entry => {
        const subject = currentBudget.subjects.find(s =>
          s.id === entry.subjectId || s.name === entry.subjectId
        );
        if (subject?.type === 'credit') {
          if (isBudgetMeasure(entry.measure)) budgetIncome += entry.amount;
          if (isActualMeasure(entry.measure)) actualIncome += entry.amount;
        } else if (subject?.type === 'debit') {
          if (isBudgetMeasure(entry.measure)) budgetExpenses += entry.amount;
          if (isActualMeasure(entry.measure)) actualExpenses += entry.amount;
        }
      });

      const budgetProfit = budgetIncome - budgetExpenses;
      const actualProfit = actualIncome - actualExpenses;

      if (showAccumulative) {
        accBudget += budgetProfit;
        accActual += actualProfit;
        return { period, [`${budgetMeasure} ${t('budget.profit')}`]: accBudget, [`${actualMeasure} ${t('budget.profit')}`]: accActual };
      }
      return { period, [`${budgetMeasure} ${t('budget.profit')}`]: budgetProfit, [`${actualMeasure} ${t('budget.profit')}`]: actualProfit };
    });
  }, [currentBudget, entries, periods, selectedPeriod, showAccumulative, budgetMeasure, actualMeasure, t]);

  // Calculate income % by subject (IncomePart vs IncomePartGoal)
  const incomePartData = useMemo(() => {
    if (!currentBudget) return [];

    // Filter entries to valid periods only (exclude period 0)
    const validEntries = entries.filter(e => e.period !== '0' && periods.includes(e.period));

    // Calculate total actual income
    const totalActualIncome = validEntries
      .filter(e => {
        const subject = currentBudget.subjects.find(s => s.id === e.subjectId || s.name === e.subjectId);
        return subject?.type === 'credit' && isActualMeasure(e.measure) && (!selectedPeriod || e.period === selectedPeriod);
      })
      .reduce((sum, e) => sum + e.amount, 0);

    // Get income part for debit subjects
    return debitSubjects.map(subject => {
      const subjectEntries = validEntries.filter(e =>
        (e.subjectId === subject.id || e.subjectId === subject.name) &&
        (!selectedPeriod || e.period === selectedPeriod)
      );

      const actual = subjectEntries
        .filter(e => isActualMeasure(e.measure))
        .reduce((sum, e) => sum + e.amount, 0);

      // Get goal from subject or sum from sections (for older budgets)
      let goal = subject.goalPercentage || 0;
      if (!goal) {
        // Try to get goal from sections (older budget structure)
        const sections = currentBudget.sections.filter(s => s.subjectId === subject.id);
        goal = sections.reduce((sum, s) => sum + (s.incomePartGoal || 0), 0);
      }

      const incomePart = totalActualIncome > 0 ? Math.round((actual / totalActualIncome) * 100) : 0;

      return {
        name: subject.name,
        [t('budget.incomePart') || '% מהכנסות']: incomePart,
        [t('budget.incomePartGoal') || '% יעד']: goal,
      };
    });
  }, [currentBudget, entries, selectedPeriod, debitSubjects, t, periods]);

  // Profit IncomePart data (for calculated profit row)
  const profitIncomePartData = useMemo(() => {
    if (!currentBudget) return [];

    // Filter entries to valid periods only (exclude period 0)
    const validEntries = entries.filter(e => e.period !== '0' && periods.includes(e.period));

    const totalActualIncome = validEntries
      .filter(e => {
        const subject = currentBudget.subjects.find(s => s.id === e.subjectId || s.name === e.subjectId);
        return subject?.type === 'credit' && isActualMeasure(e.measure) && (!selectedPeriod || e.period === selectedPeriod);
      })
      .reduce((sum, e) => sum + e.amount, 0);

    const totalActualExpenses = validEntries
      .filter(e => {
        const subject = currentBudget.subjects.find(s => s.id === e.subjectId || s.name === e.subjectId);
        return subject?.type === 'debit' && isActualMeasure(e.measure) && (!selectedPeriod || e.period === selectedPeriod);
      })
      .reduce((sum, e) => sum + e.amount, 0);

    const profit = totalActualIncome - totalActualExpenses;
    const profitPercent = totalActualIncome > 0 ? Math.round((profit / totalActualIncome) * 100) : 0;

    // Get profit goal - either from calculated subject or calculate as 100% - total expense goals
    let goalPercent = 0;
    const profitSubject = currentBudget.subjects.find(s => s.type === 'calculated');
    if (profitSubject?.goalPercentage) {
      goalPercent = profitSubject.goalPercentage;
    } else {
      // Calculate profit goal as 100% - sum of all expense goals (older budget structure)
      const totalExpenseGoal = debitSubjects.reduce((sum, subject) => {
        let subjectGoal = subject.goalPercentage || 0;
        if (!subjectGoal) {
          // Get from sections
          const sections = currentBudget.sections.filter(s => s.subjectId === subject.id);
          subjectGoal = sections.reduce((s, sec) => s + (sec.incomePartGoal || 0), 0);
        }
        return sum + subjectGoal;
      }, 0);
      goalPercent = 100 - totalExpenseGoal;
    }

    return [{
      name: t('budget.profit'),
      [t('budget.incomePart') || '% מהכנסות']: profitPercent,
      [t('budget.incomePartGoal') || '% יעד']: goalPercent,
    }];
  }, [currentBudget, entries, selectedPeriod, debitSubjects, t, periods]);

  // Subject chart data (per subject trend)
  const getSubjectChartData = (subjectName: string) => {
    if (!currentBudget) return [];
    const displayPeriods = selectedPeriod ? [selectedPeriod] : periods;
    let accBudget = 0, accActual = 0;

    return displayPeriods.map(period => {
      const periodEntries = entries.filter(e =>
        (e.subjectId === subjectName ||
         currentBudget.subjects.find(s => s.id === e.subjectId)?.name === subjectName) &&
        e.period === period
      );

      const budget = periodEntries.filter(e => isBudgetMeasure(e.measure)).reduce((sum, e) => sum + e.amount, 0);
      const actual = periodEntries.filter(e => isActualMeasure(e.measure)).reduce((sum, e) => sum + e.amount, 0);

      if (showAccumulative) {
        accBudget += budget;
        accActual += actual;
        return { period, [budgetMeasure]: accBudget, [actualMeasure]: accActual };
      }
      return { period, [budgetMeasure]: budget, [actualMeasure]: actual };
    });
  };

  // Section pie chart data for a subject - returns both actual and budget data
  // Uses same matching logic as line charts for consistency
  const getSectionPieData = (subjectName: string) => {
    if (!currentBudget) return { actual: [], budget: [] };

    const subject = currentBudget.subjects.find(s => s.name === subjectName);
    if (!subject) return { actual: [], budget: [] };

    const sections = currentBudget.sections.filter(s => s.subjectId === subject.id);

    const actualData = sections.map(section => {
      const sectionEntries = entries.filter(e => {
        // Match subject by ID, name, or lookup
        const matchesSubject = e.subjectId === subject.id ||
          e.subjectId === subject.name ||
          currentBudget.subjects.find(s => s.id === e.subjectId)?.name === subject.name;
        // Match section by name or ID
        const matchesSection = (e as any).section === section.name ||
          e.sectionId === section.id ||
          e.sectionId === section.name;
        const matchesPeriod = !selectedPeriod || e.period === selectedPeriod;
        const matchesMeasure = isActualMeasure(e.measure) || e.measure === actualMeasure;
        return matchesSubject && matchesSection && matchesPeriod && matchesMeasure;
      });
      return { name: section.name, value: sectionEntries.reduce((sum, e) => sum + e.amount, 0) };
    }).filter(d => d.value > 0);

    const budgetData = sections.map(section => {
      const sectionEntries = entries.filter(e => {
        // Match subject by ID, name, or lookup
        const matchesSubject = e.subjectId === subject.id ||
          e.subjectId === subject.name ||
          currentBudget.subjects.find(s => s.id === e.subjectId)?.name === subject.name;
        // Match section by name or ID
        const matchesSection = (e as any).section === section.name ||
          e.sectionId === section.id ||
          e.sectionId === section.name;
        const matchesPeriod = !selectedPeriod || e.period === selectedPeriod;
        const matchesMeasure = isBudgetMeasure(e.measure) || e.measure === budgetMeasure;
        return matchesSubject && matchesSection && matchesPeriod && matchesMeasure;
      });
      return { name: section.name, value: sectionEntries.reduce((sum, e) => sum + e.amount, 0) };
    }).filter(d => d.value > 0);

    return { actual: actualData, budget: budgetData };
  };

  // Section trend data
  const getSectionChartData = (subjectName: string, sectionName: string) => {
    if (!currentBudget) return [];
    const subject = currentBudget.subjects.find(s => s.name === subjectName);
    if (!subject) return [];

    // Find section by name to get its ID
    const section = currentBudget.sections.find(s => s.subjectId === subject.id && s.name === sectionName);

    const displayPeriods = selectedPeriod ? [selectedPeriod] : periods;
    let accBudget = 0, accActual = 0;

    return displayPeriods.map(period => {
      const sectionEntries = entries.filter(e =>
        (e.subjectId === subject.id || e.subjectId === subject.name) &&
        ((e as any).section === sectionName || e.sectionId === sectionName || (section && e.sectionId === section.id)) &&
        e.period === period
      );

      const budget = sectionEntries.filter(e => isBudgetMeasure(e.measure)).reduce((sum, e) => sum + e.amount, 0);
      const actual = sectionEntries.filter(e => isActualMeasure(e.measure)).reduce((sum, e) => sum + e.amount, 0);

      if (showAccumulative) {
        accBudget += budget;
        accActual += actual;
        return { period, [budgetMeasure]: accBudget, [actualMeasure]: accActual };
      }
      return { period, [budgetMeasure]: budget, [actualMeasure]: actual };
    });
  };

  // Overview pie chart data (subject distribution) - returns both actual and budget data
  // Uses same matching logic as line charts for consistency
  const getSubjectPieData = (subjectType: 'credit' | 'debit') => {
    if (!currentBudget) return { actual: [], budget: [] };

    const subjects = subjectType === 'credit' ? creditSubjects : debitSubjects;

    const actualData = subjects.map(subject => {
      const subjectEntries = entries.filter(e => {
        // Match subject by ID, name, or lookup (same as line charts)
        const matchesSubject = e.subjectId === subject.id ||
          e.subjectId === subject.name ||
          currentBudget.subjects.find(s => s.id === e.subjectId)?.name === subject.name;
        const matchesPeriod = !selectedPeriod || e.period === selectedPeriod;
        const matchesMeasure = isActualMeasure(e.measure) || e.measure === actualMeasure;
        return matchesSubject && matchesPeriod && matchesMeasure;
      });
      return { name: subject.name, value: subjectEntries.reduce((sum, e) => sum + e.amount, 0) };
    }).filter(d => d.value > 0);

    const budgetData = subjects.map(subject => {
      const subjectEntries = entries.filter(e => {
        // Match subject by ID, name, or lookup (same as line charts)
        const matchesSubject = e.subjectId === subject.id ||
          e.subjectId === subject.name ||
          currentBudget.subjects.find(s => s.id === e.subjectId)?.name === subject.name;
        const matchesPeriod = !selectedPeriod || e.period === selectedPeriod;
        const matchesMeasure = isBudgetMeasure(e.measure) || e.measure === budgetMeasure;
        return matchesSubject && matchesPeriod && matchesMeasure;
      });
      return { name: subject.name, value: subjectEntries.reduce((sum, e) => sum + e.amount, 0) };
    }).filter(d => d.value > 0);

    return { actual: actualData, budget: budgetData };
  };

  // Get sections for a subject
  const getSectionsForSubject = (subjectName: string) => {
    if (!currentBudget) return [];
    const subject = currentBudget.subjects.find(s => s.name === subjectName);
    if (!subject) return [];
    return currentBudget.sections.filter(s => s.subjectId === subject.id);
  };

  // Render line chart - converts zero values to null so they're not drawn
  const renderLineChart = (data: any[], title: string, dataKeys?: string[], isFullscreen = false) => {
    const keys = dataKeys || [actualMeasure, budgetMeasure];
    const visibleKeys = keys.filter(k => !hiddenMeasures.has(k.replace(` ${t('budget.profit')}`, '')));

    // Convert zero values to null so Recharts skips them
    const processedData = data.map(item => {
      const newItem: any = { period: item.period };
      visibleKeys.forEach(key => {
        // Convert 0 to null so the point is not drawn
        newItem[key] = item[key] === 0 ? null : item[key];
      });
      return newItem;
    });

    // Filter out periods where ALL values are null
    const filteredData = processedData.filter(item => {
      return visibleKeys.some(key => item[key] !== null && item[key] !== undefined);
    });

    const chartHeight = isFullscreen ? 450 : 200;

    return (
      <div className={`chart-card ${isFullscreen ? 'fullscreen-content' : ''}`}>
        <div className="chart-header">
          <span className="chart-title">{title}{showAccumulative ? ' מצטבר' : ''}</span>
          {!isFullscreen && <button className="chart-expand" onClick={() => setFullscreenChart({ type: 'line', data, title, dataKeys })}>⤢</button>}
        </div>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <LineChart data={filteredData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: isFullscreen ? 14 : 11 }} />
            <YAxis tick={{ fontSize: isFullscreen ? 14 : 11 }} tickFormatter={formatNumber} />
            <Tooltip formatter={(value: number) => value !== null ? formatFullNumber(value) : '-'} />
            <Legend />
            {visibleKeys.map((key, idx) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                name={key}
                stroke={idx === 0 ? ACTUAL_COLOR : BUDGET_COLOR}
                strokeWidth={isFullscreen ? 3 : 2}
                strokeDasharray={idx === 0 ? undefined : "8 4"}
                dot={{ r: isFullscreen ? 5 : 3, fill: idx === 0 ? ACTUAL_COLOR : BUDGET_COLOR }}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Render horizontal bar chart - filters out items where all values are zero
  const renderHorizontalBarChart = (data: any[], title: string, dataKeys: string[], isFullscreen = false) => {
    // Filter out items where all values are zero
    const filteredData = data.filter(item => {
      return dataKeys.some(key => item[key] !== 0 && item[key] !== undefined);
    });

    const chartHeight = isFullscreen ? Math.max(400, filteredData.length * 50) : Math.max(200, filteredData.length * 40);

    return (
      <div className={`chart-card ${isFullscreen ? 'fullscreen-content' : ''}`}>
        <div className="chart-header">
          <span className="chart-title">{title}</span>
          {!isFullscreen && <button className="chart-expand" onClick={() => setFullscreenChart({ type: 'bar', data, title, dataKeys })}>⤢</button>}
        </div>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={filteredData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" tick={{ fontSize: isFullscreen ? 14 : 11 }} />
            <YAxis dataKey="name" type="category" width={isFullscreen ? 150 : 100} tick={{ fontSize: isFullscreen ? 14 : 10 }} />
            <Tooltip />
            <Legend />
            {dataKeys.map((key, idx) => (
              <Bar key={key} dataKey={key} fill={idx === 0 ? ACTUAL_COLOR : BUDGET_COLOR} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Render double pie chart (inner = budget/planned, outer = actual)
  const renderDoublePieChart = (data: { actual: { name: string; value: number }[], budget: { name: string; value: number }[] }, title: string, isFullscreen = false) => {
    const hasActual = data.actual.length > 0;
    const hasBudget = data.budget.length > 0;

    // If no data at all, show empty state
    if (!hasActual && !hasBudget) {
      return (
        <div className={`chart-card ${isFullscreen ? 'fullscreen-content' : ''}`}>
          <div className="chart-header">
            <span className="chart-title">{title}</span>
            <button className="chart-expand" onClick={() => !isFullscreen && setFullscreenChart({ type: 'doublePie', data, title })}>⤢</button>
          </div>
          <div className="chart-empty">
            <span>{t('common.noData')}</span>
          </div>
        </div>
      );
    }

    const chartHeight = isFullscreen ? 500 : 250;
    const innerRadius = isFullscreen ? (hasActual ? 80 : 0) : (hasActual ? 0 : 0);
    const midRadius = isFullscreen ? 120 : (hasActual ? 50 : 70);
    const outerInner = isFullscreen ? 130 : (hasBudget ? 55 : 35);
    const outerRadius = isFullscreen ? 180 : 85;

    return (
      <div className={`chart-card ${isFullscreen ? 'fullscreen-content' : ''}`}>
        <div className="chart-header">
          <span className="chart-title">{title}</span>
          <div className="pie-legend-rings">
            {hasBudget && <span className="ring-legend ring-inner"><span className="ring-dot inner"></span>{budgetMeasure}</span>}
            {hasActual && <span className="ring-legend ring-outer"><span className="ring-dot outer"></span>{actualMeasure}</span>}
          </div>
          {!isFullscreen && <button className="chart-expand" onClick={() => setFullscreenChart({ type: 'doublePie', data, title })}>⤢</button>}
        </div>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <PieChart>
            {/* Inner ring - Budget/Planned */}
            {hasBudget && (
              <Pie
                data={data.budget}
                cx="50%"
                cy="50%"
                innerRadius={innerRadius}
                outerRadius={midRadius}
                dataKey="value"
                label={!hasActual || isFullscreen ? ({ name, percent }) => percent > 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : '' : false}
                labelLine={isFullscreen}
              >
                {data.budget.map((_, idx) => (
                  <Cell key={`budget-${idx}`} fill={COLORS[idx % COLORS.length]} opacity={0.5} />
                ))}
              </Pie>
            )}
            {/* Outer ring - Actual */}
            {hasActual && (
              <Pie
                data={data.actual}
                cx="50%"
                cy="50%"
                innerRadius={outerInner}
                outerRadius={outerRadius}
                dataKey="value"
                label={({ name, percent }) => percent > 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : ''}
                labelLine={isFullscreen}
              >
                {data.actual.map((_, idx) => (
                  <Cell key={`actual-${idx}`} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
            )}
            <Tooltip formatter={(value: number) => formatFullNumber(value)} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Simple single pie chart (for backward compatibility)
  const renderPieChart = (data: { name: string; value: number }[], title: string) => (
    <div className="chart-card">
      <div className="chart-header">
        <span className="chart-title">{title}</span>
        <button className="chart-expand">⤢</button>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={35}
            outerRadius={70}
            dataKey="value"
            label={({ name, percent }) => percent > 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : ''}
            labelLine={false}
          >
            {data.map((_, idx) => (
              <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value: number) => formatFullNumber(value)} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );

  // Get current subjects list based on main tab
  const currentSubjects = mainTab === 'credit' ? creditSubjects : mainTab === 'debit' ? debitSubjects : [];

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>{t('general.loading')}</p>
      </div>
    );
  }

  if (!currentBudget) {
    return (
      <div className="empty-state">
        <h2>{t('budget.noBudgets')}</h2>
        <p>{t('budget.createFirst')}</p>
      </div>
    );
  }

  return (
    <div className="charts-page">
      {/* Header Controls */}
      <div className="charts-controls">
        <div className="control-group">
          <span className="control-label">{t('settings.measures') || 'מדדים'}</span>
          {measures.map(measure => (
            <button
              key={measure}
              className={`measure-toggle ${hiddenMeasures.has(measure) ? 'hidden' : 'active'}`}
              onClick={() => toggleMeasure(measure)}
            >
              {measure}
            </button>
          ))}
        </div>

        <div className="control-group">
          <span className="control-label">{chartsPerRow} גרפים/שורה</span>
          <select
            value={chartsPerRow}
            onChange={(e) => setChartsPerRow(Number(e.target.value))}
            className="control-select"
          >
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={4}>4</option>
          </select>
        </div>

        <div className="control-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showAccumulative}
              onChange={(e) => setShowAccumulative(e.target.checked)}
            />
            מצטבר
          </label>
        </div>

        <div className="control-group">
          <select
            value={selectedPeriod || ''}
            onChange={(e) => setSelectedPeriod(e.target.value || null)}
            className="control-select"
          >
            <option value="">{t('budget.allPeriods')}</option>
            {periods.map(period => (
              <option key={period} value={period}>{period}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="view-tabs">
        <button
          className={`view-tab ${mainTab === 'overview' ? 'active' : ''}`}
          onClick={() => { setMainTab('overview'); setSubTab('overview'); }}
        >
          {t('budget.overview') || 'מבט על'}
        </button>
        <button
          className={`view-tab ${mainTab === 'credit' ? 'active' : ''}`}
          onClick={() => { setMainTab('credit'); setSubTab('overview'); }}
        >
          {t('settings.credit') || 'זכות'}
        </button>
        <button
          className={`view-tab ${mainTab === 'debit' ? 'active' : ''}`}
          onClick={() => { setMainTab('debit'); setSubTab('overview'); }}
        >
          {t('settings.debit') || 'חובה'}
        </button>
      </div>

      {/* Sub Tabs for Credit/Debit */}
      {(mainTab === 'credit' || mainTab === 'debit') && (
        <div className="sub-tabs">
          <button
            className={`sub-tab ${subTab === 'overview' ? 'active' : ''}`}
            onClick={() => setSubTab('overview')}
          >
            {t('budget.overview') || 'מבט על'}
          </button>
          {currentSubjects.map(subject => (
            <button
              key={subject.id}
              className={`sub-tab ${subTab === subject.name ? 'active' : ''}`}
              onClick={() => setSubTab(subject.name)}
            >
              {subject.name}
            </button>
          ))}
        </div>
      )}

      {/* Charts Grid */}
      <div className="charts-grid" style={{ gridTemplateColumns: `repeat(${chartsPerRow}, 1fr)` }}>

        {/* OVERVIEW TAB */}
        {mainTab === 'overview' && (
          <>
            {/* IncomePart vs Goal for Debit subjects */}
            {renderHorizontalBarChart(
              incomePartData,
              `${t('budget.expenses')} - % ${t('budget.income')}`,
              [t('budget.incomePart') || '% מהכנסות', t('budget.incomePartGoal') || '% יעד']
            )}

            {/* IncomePart vs Goal for Profit */}
            {renderHorizontalBarChart(
              profitIncomePartData,
              `${t('budget.profit')} - % ${t('budget.income')}`,
              [t('budget.incomePart') || '% מהכנסות', t('budget.incomePartGoal') || '% יעד']
            )}

            {/* Credits by Period */}
            {renderLineChart(creditsByPeriod, `${t('budget.income')} ${actualMeasure} / ${budgetMeasure}`)}

            {/* Profits by Period */}
            {renderLineChart(
              profitsByPeriod,
              `${t('budget.profit')}`,
              [`${actualMeasure} ${t('budget.profit')}`, `${budgetMeasure} ${t('budget.profit')}`]
            )}
          </>
        )}

        {/* CREDIT/DEBIT OVERVIEW */}
        {(mainTab === 'credit' || mainTab === 'debit') && subTab === 'overview' && (
          <>
            {/* Double pie chart showing distribution by subject (inner=budget, outer=actual) */}
            {renderDoublePieChart(getSubjectPieData(mainTab), `${mainTab === 'credit' ? t('budget.income') : t('budget.expenses')} - ${t('budget.overview')}`)}

            {/* Per-subject trend charts */}
            {currentSubjects.map(subject => (
              <div key={subject.id}>
                {renderLineChart(getSubjectChartData(subject.name), subject.name)}
              </div>
            ))}
          </>
        )}

        {/* SPECIFIC SUBJECT TAB */}
        {(mainTab === 'credit' || mainTab === 'debit') && subTab !== 'overview' && (
          <>
            {/* Double pie chart by section (inner=budget, outer=actual) */}
            {renderDoublePieChart(getSectionPieData(subTab), `${subTab} - סעיפים`)}

            {/* Subject trend (accumulative) */}
            {renderLineChart(getSubjectChartData(subTab), `${subTab}`)}

            {/* Per-section charts */}
            {getSectionsForSubject(subTab).map(section => (
              <div key={section.id}>
                {renderLineChart(getSectionChartData(subTab, section.name), section.name)}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Fullscreen Modal */}
      {fullscreenChart && (
        <div className="fullscreen-overlay" onClick={() => setFullscreenChart(null)}>
          <div className="fullscreen-modal" onClick={(e) => e.stopPropagation()}>
            <button className="fullscreen-close" onClick={() => setFullscreenChart(null)}>✕</button>
            {fullscreenChart.type === 'line' && renderLineChart(fullscreenChart.data, fullscreenChart.title, fullscreenChart.dataKeys, true)}
            {fullscreenChart.type === 'bar' && renderHorizontalBarChart(fullscreenChart.data, fullscreenChart.title, fullscreenChart.dataKeys || [], true)}
            {fullscreenChart.type === 'doublePie' && renderDoublePieChart(fullscreenChart.data, fullscreenChart.title, true)}
          </div>
        </div>
      )}

      <style>{`
        .charts-page {
          max-width: 100%;
          margin: 0 auto;
          padding: 0 1rem;
        }

        .charts-controls {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          padding: 0.75rem 1rem;
          background: var(--color-surface);
          border-radius: var(--radius);
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .control-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .control-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--color-text-secondary);
        }

        .measure-toggle {
          padding: 0.375rem 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background: var(--color-surface);
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s;
        }

        .measure-toggle.active {
          background: var(--color-primary);
          color: white;
          border-color: var(--color-primary);
        }

        .measure-toggle.hidden {
          background: #f3f4f6;
          color: #9ca3af;
          border-color: #e5e7eb;
        }

        .control-select {
          padding: 0.375rem 0.75rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background: var(--color-surface);
          font-size: 0.875rem;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          cursor: pointer;
          font-size: 0.875rem;
        }

        .view-tabs {
          display: flex;
          gap: 0;
          margin-bottom: 0;
          border-bottom: 2px solid var(--color-border);
        }

        .view-tab {
          padding: 0.75rem 1.5rem;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--color-text-secondary);
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
          transition: all 0.15s;
        }

        .view-tab:hover {
          color: var(--color-text);
        }

        .view-tab.active {
          color: var(--color-primary);
          border-bottom-color: var(--color-primary);
        }

        .sub-tabs {
          display: flex;
          gap: 0;
          margin-bottom: 1rem;
          border-bottom: 1px solid var(--color-border);
          background: #fafafa;
          padding: 0 0.5rem;
          overflow-x: auto;
        }

        .sub-tab {
          padding: 0.5rem 1rem;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 0.8rem;
          color: var(--color-text-secondary);
          white-space: nowrap;
          transition: all 0.15s;
        }

        .sub-tab:hover {
          color: var(--color-text);
          background: #f0f0f0;
        }

        .sub-tab.active {
          color: var(--color-primary);
          background: white;
          font-weight: 500;
        }

        .charts-grid {
          display: grid;
          gap: 1rem;
          margin-top: 1rem;
        }

        .chart-card {
          background: var(--color-surface);
          border-radius: var(--radius);
          border: 1px solid var(--color-border);
          overflow: hidden;
        }

        .chart-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem 0.75rem;
          border-bottom: 1px solid var(--color-border);
          background: #fafafa;
        }

        .chart-title {
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--color-text-secondary);
        }

        .chart-expand {
          width: 24px;
          height: 24px;
          padding: 0;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          background: var(--color-surface);
          cursor: pointer;
          font-size: 0.75rem;
          color: var(--color-text-secondary);
        }

        .chart-expand:hover {
          background: var(--color-background);
        }

        .pie-legend-rings {
          display: flex;
          gap: 1rem;
          font-size: 0.7rem;
        }

        .ring-legend {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .ring-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid #3b82f6;
        }

        .ring-dot.inner {
          background: rgba(59, 130, 246, 0.4);
        }

        .ring-dot.outer {
          background: #3b82f6;
        }

        .chart-empty {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 200px;
          color: var(--color-text-secondary);
          font-size: 0.875rem;
        }

        .empty-state {
          text-align: center;
          padding: 3rem;
        }

        .loading-screen {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
        }

        /* Fullscreen Modal */
        .fullscreen-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 2rem;
        }

        .fullscreen-modal {
          background: white;
          border-radius: var(--radius-lg);
          width: 100%;
          max-width: 1200px;
          max-height: 90vh;
          overflow: auto;
          position: relative;
        }

        .fullscreen-close {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          width: 32px;
          height: 32px;
          border: none;
          background: var(--color-surface);
          border-radius: 50%;
          cursor: pointer;
          font-size: 1rem;
          color: var(--color-text-secondary);
          z-index: 10;
          box-shadow: var(--shadow);
        }

        .fullscreen-close:hover {
          background: var(--color-bg);
          color: var(--color-text);
        }

        .fullscreen-content {
          border: none;
        }

        .fullscreen-content .chart-header {
          padding: 1rem 1.5rem;
        }

        .fullscreen-content .chart-title {
          font-size: 1.1rem;
        }

        .fullscreen-content .pie-legend-rings {
          font-size: 0.9rem;
        }

        .fullscreen-content .ring-dot {
          width: 16px;
          height: 16px;
        }
      `}</style>
    </div>
  );
};
